// filepath: apps/web/src/components/AppShell/MobileDrawer.tsx
//
// Slide-in drawer used below the md breakpoint as the primary
// nav surface. Re-uses the same <Sidebar> + <SidebarSection>
// tree as desktop, so section collapse state stays in sync via
// the localStorage keys owned by SidebarSection.
//
// a11y contract:
//   - The toggle button gets `aria-controls="osd-mobile-drawer"`
//     and `aria-expanded` reflecting open state.
//   - The drawer root is role="dialog" + aria-modal="true".
//   - When the drawer opens, focus moves into the drawer (to
//     the first focusable child).
//   - Tab + Shift+Tab cycle inside the drawer while it's open
//     (focus trap).
//   - Escape closes the drawer and returns focus to the toggle.
//   - Route changes close the drawer (so a tapped link inside
//     doesn't leave the drawer open over the new page).
//
// Closes #33.

import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from '@tanstack/react-router';
import { Sidebar, type NavSection } from '@osd/ui/nav';

export interface MobileDrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly sections: ReadonlyArray<NavSection>;
  /** Ref to the toggle button — used to return focus on close. */
  readonly toggleRef: React.RefObject<HTMLButtonElement | null>;
}

const DRAWER_ID = 'osd-mobile-drawer';

/** Return the first focusable element inside `root`, or null. */
function firstFocusable(root: HTMLElement): HTMLElement | null {
  const selector =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const list = root.querySelectorAll<HTMLElement>(selector);
  for (const el of Array.from(list)) {
    // Skip elements that are visually hidden.
    if (el.offsetParent !== null) return el;
  }
  return null;
}

/** Return all focusable elements inside `root`. */
function focusableElements(root: HTMLElement): HTMLElement[] {
  const selector =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const list = root.querySelectorAll<HTMLElement>(selector);
  return Array.from(list).filter((el) => el.offsetParent !== null);
}

export function MobileDrawer({
  open,
  onClose,
  sections,
  toggleRef,
}: MobileDrawerProps): JSX.Element {
  const drawerRef = useRef<HTMLDialogElement | null>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);
  const location = useLocation();

  // 1. Close on route change — once the URL flips the drawer is
  //    no longer the right surface to be looking at.
  useEffect(() => {
    if (open) onClose();
    // We intentionally depend on location.pathname so every
    // navigation closes the drawer, not just the first render
    // after `open` flips to true.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // 2. Open/close side effects: focus management + body scroll
  //    lock + Escape-to-close.
  useEffect(() => {
    if (!open) return;

    // Remember whatever had focus before we opened the drawer
    // so we can restore it on close. Fall back to the toggle
    // button (passed in via toggleRef).
    lastFocusedRef.current = (document.activeElement as HTMLElement | null) ?? toggleRef.current;

    // Move focus into the drawer.
    const drawer = drawerRef.current;
    if (drawer !== null) {
      const first = firstFocusable(drawer);
      first?.focus();
    }

    // Lock body scroll so the drawer doesn't drift on iOS Safari
    // when the user tries to scroll past it.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function handleKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      // Focus trap: cycle inside the drawer.
      const root = drawerRef.current;
      if (root === null) return;
      const items = focusableElements(root);
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items.at(-1);
      if (first === undefined || last === undefined) return;
      const active = document.activeElement as HTMLElement | null;
      if (event.shiftKey) {
        if (active === first || !root.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
      // Return focus on close.
      const restore =
        lastFocusedRef.current !== null && document.contains(lastFocusedRef.current)
          ? lastFocusedRef.current
          : toggleRef.current;
      restore?.focus();
    };
  }, [open, onClose, toggleRef]);

  const handleOverlayClick = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <>
      <div
        className={`osd-drawer-backdrop${open ? ' osd-drawer-backdrop--open' : ''}`}
        onClick={handleOverlayClick}
        aria-hidden="true"
      />
      <dialog
        ref={drawerRef}
        id={DRAWER_ID}
        className={`osd-drawer${open ? ' osd-drawer--open' : ''}`}
        aria-label="Primary navigation"
      >
        <Sidebar sections={sections} />
      </dialog>
    </>
  );
}

export { DRAWER_ID as MOBILE_DRAWER_ID };
